import React, { useState, useEffect } from 'react';
import { 
  DollarOutlined, 
  CalendarOutlined, 
  PictureOutlined,
  DownOutlined,
  TeamOutlined,
  GlobalOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';
import { DatePicker, Select, InputNumber } from 'antd';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

const objectiveOptions = [
  { value: 'REACH', label: 'Reach - Maximize people who see your ads' },
  { value: 'TRAFFIC', label: 'Traffic - Drive visits to your business' },
  { value: 'ENGAGEMENT', label: 'Engagement - Get more interactions' },
  { value: 'CONVERSIONS', label: 'Conversions - Drive specific actions' },
  { value: 'BRAND_AWARENESS', label: 'Brand Awareness - Increase awareness' },
];

const ctaOptions = [
  { value: 'LEARN_MORE', label: 'Learn More' },
  { value: 'SHOP_NOW', label: 'Shop Now' },
  { value: 'SIGN_UP', label: 'Sign Up' },
  { value: 'GET_QUOTE', label: 'Get Quote' },
  { value: 'CALL_NOW', label: 'Call Now' },
  { value: 'CONTACT_US', label: 'Contact Us' },
];

const ConfigureMetaAds = ({ campaignData, onUpdate, onNext }) => {
  // Extract locations from stops or routes
  const getLocationAddresses = () => {
    // For stop-based campaigns, get addresses from stops
    if (campaignData.campaignType === 'stop' && campaignData.selectedStopsData) {
      return campaignData.selectedStopsData
        .map(stop => ({
          address: stop.address || `${stop.coords?.lat}, ${stop.coords?.lng}`,
          latitude: stop.coords?.lat,
          longitude: stop.coords?.lng,
          city: stop.city,
          state: stop.state,
          postcode: stop.postcode
        }))
        .filter(loc => loc.latitude && loc.longitude);
    }
    // For route-based campaigns, get start/end addresses from routes
    if (campaignData.campaignType === 'route' && campaignData.selectedRoutesData) {
      const locations = [];
      campaignData.selectedRoutesData.forEach(route => {
        if (route.startAddress && route.coordinates?.[0]) {
          locations.push({
            address: route.startAddress,
            latitude: route.coordinates[0][0],
            longitude: route.coordinates[0][1]
          });
        }
        if (route.endAddress && route.coordinates?.[route.coordinates.length - 1]) {
          const endCoords = route.coordinates[route.coordinates.length - 1];
          locations.push({
            address: route.endAddress,
            latitude: endCoords[0],
            longitude: endCoords[1]
          });
        }
      });
      return locations.filter(loc => loc.latitude && loc.longitude);
    }
    return [];
  };

  const locationData = getLocationAddresses();
  const locationAddresses = locationData.map(loc => loc.address);

  const [campaignName, setCampaignName] = useState(campaignData.metaAds?.name || '');
  const [objective, setObjective] = useState(campaignData.metaAds?.objective || 'REACH');
  const [budgetType, setBudgetType] = useState(campaignData.metaAds?.budgetType || 'daily');
  const [dailyBudget, setDailyBudget] = useState(campaignData.metaAds?.dailyBudget || 20);
  const [totalBudget, setTotalBudget] = useState(campaignData.metaAds?.totalBudget || 500);
  const [schedule, setSchedule] = useState(campaignData.metaAds?.schedule || null);
  const [headline, setHeadline] = useState(campaignData.metaAds?.headline || '');
  const [description, setDescription] = useState(campaignData.metaAds?.description || '');
  const [imageUrl, setImageUrl] = useState(campaignData.metaAds?.imageUrl || '');
  const [cta, setCta] = useState(campaignData.metaAds?.cta || 'LEARN_MORE');
  const [radiusKm, setRadiusKm] = useState(campaignData.metaAds?.radiusKm || 5);

  useEffect(() => {
    onUpdate({
      metaAds: {
        name: campaignName,
        objective,
        budgetType,
        dailyBudget,
        totalBudget,
        schedule,
        headline,
        description,
        imageUrl,
        cta,
        radiusKm,
        locations: locationData,
        locationAddresses: locationAddresses,
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignName, objective, budgetType, dailyBudget, totalBudget, schedule, headline, description, imageUrl, cta, radiusKm]);

  const handleContinue = () => {
    console.log('ConfigureMetaAds - Continue clicked');
    console.log('Campaign Name:', campaignName);
    console.log('Schedule:', schedule);
    console.log('Campaign Data:', campaignData);
    onNext();
  };

  const disabledDate = (current) => {
    // Disable dates before today
    return current && current < dayjs().startOf('day');
  };

  return (
    <div className="step-container">
      <div className="sms-header-icon">
        <TeamOutlined />
      </div>
      <h1 className="step-title">Configure Meta Ads Campaign</h1>
      <p className="step-subtitle">Set up your Facebook & Instagram ads</p>

      {/* Campaign Configuration - 3 Column Grid with Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '24px' }}>
        
        {/* Campaign Name Card */}
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #f0f0f0',
          transition: 'all 0.3s ease'
        }}>
          <label className="form-label" style={{ 
            fontWeight: 600, 
            fontSize: '13px', 
            color: 'var(--ink)',
            marginBottom: '8px',
            display: 'block'
          }}>Campaign Name</label>
          <input
            type="text"
            className="form-input"
            placeholder="e.g., Route-Based Awareness Campaign"
            value={campaignName}
            onChange={(e) => setCampaignName(e.target.value)}
            style={{
              borderRadius: '8px',
              border: '1px solid var(--paper-300)',
              padding: '10px 12px'
            }}
          />
        </div>

        {/* Objective Card */}
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #f0f0f0',
          transition: 'all 0.3s ease'
        }}>
          <label className="form-label" style={{ 
            fontWeight: 600, 
            fontSize: '13px', 
            color: 'var(--ink)',
            marginBottom: '8px',
            display: 'block'
          }}>Campaign Objective</label>
          <Select
            style={{ width: '100%', height: 44 }}
            value={objective}
            onChange={setObjective}
            options={objectiveOptions}
          />
        </div>

        {/* Call to Action Card */}
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #f0f0f0',
          transition: 'all 0.3s ease'
        }}>
          <label className="form-label" style={{ 
            fontWeight: 600, 
            fontSize: '13px', 
            color: 'var(--ink)',
            marginBottom: '8px',
            display: 'block'
          }}>Call to Action</label>
          <Select
            style={{ width: '100%', height: 44 }}
            value={cta}
            onChange={setCta}
            options={ctaOptions}
          />
        </div>
      </div>

      {/* Budget & Schedule Configuration - 3 Column Grid with Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '24px' }}>
        
        {/* Budget Type Card */}
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #f0f0f0',
          transition: 'all 0.3s ease'
        }}>
          <label className="form-label" style={{ 
            fontWeight: 600, 
            fontSize: '13px', 
            color: 'var(--ink)',
            marginBottom: '8px',
            display: 'block'
          }}>
            <DollarOutlined /> Budget Type
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className={`cooldown-btn ${budgetType === 'daily' ? 'selected' : ''}`}
              onClick={() => setBudgetType('daily')}
              style={{ 
                flex: 1,
                borderRadius: '8px',
                padding: '10px',
                fontSize: '13px'
              }}
            >
              Daily
            </button>
            <button
              className={`cooldown-btn ${budgetType === 'total' ? 'selected' : ''}`}
              onClick={() => setBudgetType('total')}
              style={{ 
                flex: 1,
                borderRadius: '8px',
                padding: '10px',
                fontSize: '13px'
              }}
            >
              Total
            </button>
          </div>
        </div>

        {/* Budget Amount Card */}
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #f0f0f0',
          transition: 'all 0.3s ease'
        }}>
          <label className="form-label" style={{ 
            fontWeight: 600, 
            fontSize: '13px', 
            color: 'var(--ink)',
            marginBottom: '8px',
            display: 'block'
          }}>
            {budgetType === 'daily' ? 'Daily Budget' : 'Total Budget'}
          </label>
          <InputNumber
            style={{ width: '100%', height: 44 }}
            min={1}
            value={budgetType === 'daily' ? dailyBudget : totalBudget}
            onChange={budgetType === 'daily' ? setDailyBudget : setTotalBudget}
            prefix="$"
            placeholder={budgetType === 'daily' ? 'Daily budget' : 'Total budget'}
          />
        </div>

        {/* Campaign Schedule Card */}
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #f0f0f0',
          transition: 'all 0.3s ease'
        }}>
          <label className="form-label" style={{ 
            fontWeight: 600, 
            fontSize: '13px', 
            color: 'var(--ink)',
            marginBottom: '8px',
            display: 'block'
          }}>
            <CalendarOutlined /> Campaign Schedule
          </label>
          <RangePicker
            style={{ width: '100%', height: 44 }}
            value={schedule}
            onChange={setSchedule}
            format="MMM D, YYYY"
            placeholder={['Start Date', 'End Date']}
            disabledDate={disabledDate}
          />
        </div>
      </div>

      {/* Creative Content - Full Width Card with 3 Lines */}
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        border: '1px solid #f0f0f0',
        marginBottom: '24px',
        width: 'calc(100% + 0px)'
      }}>
        <div style={{ marginBottom: '16px', fontSize: '15px', fontWeight: 600, color: 'var(--ink)' }}>
          <PictureOutlined /> Ad Creative
        </div>

        {/* Line 1: Ad Headline */}
        <div style={{ marginBottom: '16px' }}>
          <label className="form-label" style={{ 
            fontWeight: 600, 
            fontSize: '13px', 
            color: 'var(--ink)',
            marginBottom: '8px',
            display: 'block'
          }}>Ad Headline</label>
          <input
            type="text"
            className="form-input"
            placeholder="e.g., We're in Your Neighborhood Today!"
            value={headline}
            onChange={(e) => setHeadline(e.target.value)}
            maxLength={40}
            style={{
              width: '100%',
              borderRadius: '8px',
              border: '1px solid var(--paper-300)',
              padding: '10px 12px'
            }}
          />
          <span className="form-hint" style={{ fontSize: '11px', color: 'var(--ink-200)', marginTop: '4px', display: 'block' }}>
            {headline.length}/40 characters
          </span>
        </div>

        {/* Line 2: Ad Description */}
        <div style={{ marginBottom: '16px' }}>
          <label className="form-label" style={{ 
            fontWeight: 600, 
            fontSize: '13px', 
            color: 'var(--ink)',
            marginBottom: '8px',
            display: 'block'
          }}>Ad Description</label>
          <textarea
            className="form-textarea"
            placeholder="e.g., Our team is servicing homes in your area. Get a special offer today!"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            maxLength={125}
            style={{
              width: '100%',
              borderRadius: '8px',
              border: '1px solid var(--paper-300)',
              padding: '10px 12px',
              resize: 'vertical'
            }}
          />
          <span className="form-hint" style={{ fontSize: '11px', color: 'var(--ink-200)', marginTop: '4px', display: 'block' }}>
            {description.length}/125 characters
          </span>
        </div>

        {/* Line 3: Ad Image URL */}
        <div>
          <label className="form-label" style={{ 
            fontWeight: 600, 
            fontSize: '13px', 
            color: 'var(--ink)',
            marginBottom: '8px',
            display: 'block'
          }}>Ad Image URL</label>
          <input
            type="text"
            className="form-input"
            placeholder="https://example.com/image.jpg"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            style={{
              width: '100%',
              borderRadius: '8px',
              border: '1px solid var(--paper-300)',
              padding: '10px 12px'
            }}
          />
          <span className="form-hint" style={{ fontSize: '11px', color: 'var(--ink-200)', marginTop: '4px', display: 'block' }}>
            Recommended: 1200x628px
          </span>
        </div>
      </div>

      {/* Location Targeting & Radius - Full Width Card */}
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        border: '1px solid #f0f0f0',
        marginBottom: '24px',
        width: 'calc(100% + 0px)'
      }}>
        <div style={{ marginBottom: '16px', fontSize: '15px', fontWeight: 600, color: 'var(--ink)' }}>
          <EnvironmentOutlined /> Location Targeting
        </div>

        {/* Targeting Radius */}
        <div style={{ marginBottom: '16px' }}>
          <label className="form-label" style={{ 
            fontWeight: 600, 
            fontSize: '13px', 
            color: 'var(--ink)',
            marginBottom: '8px',
            display: 'block'
          }}>Targeting Radius (km)</label>
          <InputNumber
            style={{ width: '100%', height: 44 }}
            min={1}
            max={80}
            value={radiusKm}
            onChange={setRadiusKm}
            placeholder="Radius around each location"
          />
          <span className="form-hint" style={{ fontSize: '11px', color: 'var(--ink-200)', marginTop: '4px', display: 'block' }}>
            Facebook will target people within {radiusKm}km of each location
          </span>
        </div>

        {/* Target Locations */}
        <div>
          <label className="form-label" style={{ 
            fontWeight: 600, 
            fontSize: '13px', 
            color: 'var(--ink)',
            marginBottom: '8px',
            display: 'block'
          }}>
            <GlobalOutlined /> Target Locations
          </label>
          {locationAddresses.length > 0 ? (
            <div style={{ 
              background: '#f0f9ff', 
              border: '1px solid #bae6fd', 
              borderRadius: 8, 
              padding: 16,
              maxHeight: 300,
              overflowY: 'auto'
            }}>
              <div style={{ fontSize: 13, color: '#0369a1', marginBottom: 12, fontWeight: 500 }}>
                {locationAddresses.length} location{locationAddresses.length > 1 ? 's' : ''} targeted:
              </div>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(5, 1fr)', 
                gap: '12px',
                alignItems: 'start'
              }}>
                {locationAddresses.map((addr, idx) => (
                  <div key={idx} style={{ 
                    background: '#fff',
                    border: '1px solid #bae6fd',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    fontSize: 13, 
                    color: '#0c4a6e',
                    lineHeight: 1.4,
                    wordBreak: 'break-word',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                  }}>
                    <div style={{ fontWeight: 500, marginBottom: '2px', color: '#0369a1' }}>
                      #{idx + 1}
                    </div>
                    {addr}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ 
              background: '#fef3c7', 
              border: '1px solid #fbbf24', 
              borderRadius: 8, 
              padding: 12,
              fontSize: 14,
              color: '#92400e'
            }}>
              ⚠️ No locations selected. Please select stops or routes in previous steps.
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="campaign-footer">
        <div className="footer-info">
          Configure Meta Ads settings
        </div>
        <button 
          className="continue-btn" 
          onClick={handleContinue}
          disabled={!campaignName || !schedule}
        >
          Continue
          <DownOutlined />
        </button>
      </div>
    </div>
  );
};

export default ConfigureMetaAds;
